import type {
  DatasetImageSectionGalleryItem,
  DatasetImageSectionProps,
} from "./dataset-image-section.type";
import type { GalleryAction } from "@/ui/gallery/gallery.type";

import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import {
  downloadDatasetImageFile,
  isDatasetImageDownloadable,
} from "@/modules/dataset-image";
import { DatasetImageItem } from "@/modules/dataset-image/dataset-image-item";
import { DatasetImageThumbnail } from "@/modules/dataset-image/dataset-image-thumbnail";
import { DatasetImageUpload } from "@/modules/dataset-image/dataset-image-upload";
import {
  isFileRemoved,
  isFileUploaded,
  isFileUploading,
} from "@/modules/upload-file";
import { Gallery } from "@/ui/gallery";

import { useDatasetImageSectionFiles } from "./dataset-image-section.hook";

import "./dataset-image-section.style.less";

export const DatasetImageSection = ({
  template,
  datasets,
  disabled,
  loading,
}: DatasetImageSectionProps) => {
  const { t } = useTranslation();

  const { fileList, onFileUploaded, editDataset, deleteDataset } =
    useDatasetImageSectionFiles(datasets);

  const galleryItems = fileList.map((file): DatasetImageSectionGalleryItem => {
    const dataset = datasets.find((dataset) => `${dataset.id}` === file.uid);
    return {
      key: `${dataset?.id ?? file.uid}`,
      title: dataset?.name ?? file.name ?? "",
      description: dataset?.description ?? "",
      node: <DatasetImageThumbnail imageFile={file} dataset={dataset} />,
      previewNode: <DatasetImageItem imageFile={file} dataset={dataset} />,
      thumbnailClassName: classNames({
        "dataset-image-uploading": isFileUploading(file),
        "dataset-image-deleted": isFileRemoved(file),
      }),
      data: { file, dataset },
    };
  });

  return (
    <div className="dataset-image-section">
      <DatasetImageUpload
        template={template}
        disabled={disabled}
        loading={loading}
        fileList={fileList}
        listType="picture-card"
        itemRender={() => null}
        onFileUploaded={onFileUploaded}
      />
      <Gallery
        items={galleryItems}
        renderExtraActions={(item) => {
          let extraActions: GalleryAction[] = [];
          if (!item.data) return [];

          const { file, dataset } = item.data;
          const isUploaded = isFileUploaded(file);
          const isEditable = !disabled && isUploaded;

          if (isEditable && dataset) {
            extraActions = [
              {
                key: "edit",
                title: t("global:edit"),
                icon: <EditOutlined />,
                onClick: () => editDataset(dataset),
              },
              {
                key: "delete",
                title: t("global:delete"),
                icon: <DeleteOutlined />,
                onClick: () => deleteDataset(file, dataset),
              },
            ];
          }

          if (isEditable && isDatasetImageDownloadable(dataset)) {
            extraActions.push({
              key: "download",
              title: t("global:downloadPicture"),
              icon: <DownloadOutlined />,
              onClick: () =>
                downloadDatasetImageFile({ imageFile: file, dataset }),
            });
          }
          return extraActions;
        }}
      />
    </div>
  );
};
